import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, Spinner } from 'react-bootstrap';
import { useSyncStateBySyncId } from '../hooks/useSyncState';

// Module-level map of claimId → sync_id. Survives component unmount/remount,
// so a SyncButton keeps following "its" sync even if the parent re-renders
// it out and back (loading flash, tab switch, etc.). Callers provide a
// stable claimId — distinct from any other SyncButton in the app.
const buttonSyncClaims: Map<string, string> = new Map();

interface SyncButtonProps {
  // claimId is the stable identity for this specific button instance
  // across unmount/remount. Must be unique per button (e.g.
  // `qb-sync-${integrationId}`, `qb-force-sync-${integrationId}`,
  // `events-sync`, etc.).
  claimId: string;
  // onClick returns the sync_id of the sync this click initiated, or
  // undefined if no sync was started. The button subscribes to events
  // for that specific sync — so multiple SyncButtons targeting the
  // same integration are fully independent, and unrelated syncs
  // (periodic, webhook, other tab) never affect this button.
  onClick: () => Promise<string | undefined>;
  disabled?: boolean;
  variant?: string;
  size?: 'sm' | 'lg';
  buttonText?: string;
  icon?: string;
}

export const SyncButton: React.FC<SyncButtonProps> = ({
  claimId,
  onClick,
  disabled = false,
  variant = 'outline-primary',
  size = 'sm',
  buttonText = 'Sync',
  icon = 'bi-arrow-clockwise',
}) => {
  // Initialize from module store so an unmount/remount cycle doesn't lose
  // the claim on an in-flight sync.
  const [mySyncId, setMySyncId] = useState<string | undefined>(() => buttonSyncClaims.get(claimId));
  const syncState = useSyncStateBySyncId(mySyncId);
  const isSyncing = !!(syncState && (syncState.status === 'started' || syncState.status === 'progress'));
  const isCompleted = !!(syncState && syncState.status === 'completed');
  const hasError = !!(syncState && (syncState.status === 'error' || syncState.status === 'failed'));

  const showTooltip = isSyncing || isCompleted || hasError;
  const buttonRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Get accumulated messages or fallback to current message
  const messages = hasError
    ? [syncState?.error || 'Sync failed']
    : isCompleted
    ? ['Sync completed']
    : (syncState as any)?.messages || (syncState?.progress?.message ? [syncState.progress.message] : ['Syncing...']);

  // Debug logging for tooltip messages
  if (isSyncing) {
    console.log(`[SyncButton TOOLTIP] sync ${mySyncId}:`, messages);
  }

  // Update tooltip position when syncing state changes or on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current && showTooltip) {
        const rect = buttonRef.current.getBoundingClientRect();
        const tooltipWidth = 400; // maxWidth of tooltip
        const halfTooltip = tooltipWidth / 2;
        const centerX = rect.left + rect.width / 2 + window.scrollX;

        // Clamp to viewport so tooltip doesn't overflow right edge
        const viewportWidth = document.documentElement.clientWidth;
        const clampedLeft = Math.min(centerX, viewportWidth - halfTooltip - 8);

        setTooltipPosition({
          top: rect.top + window.scrollY,
          left: Math.max(halfTooltip + 8, clampedLeft),
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showTooltip]);

  const tooltipBgColor = hasError ? '#dc3545' : isCompleted ? '#198754' : '#000';

  const tooltip = showTooltip ? (
    <div
      style={{
        position: 'absolute',
        top: tooltipPosition.top - 8,
        left: tooltipPosition.left,
        transform: 'translate(-50%, -100%)',
        backgroundColor: tooltipBgColor,
        color: '#fff',
        padding: '6px 10px',
        borderRadius: '4px',
        fontSize: '12px',
        whiteSpace: 'pre',
        zIndex: 9999,
        pointerEvents: 'none',
        maxWidth: '400px',
        wordBreak: 'break-word' as const,
      }}
    >
      {messages.join('\n')}
      <div
        style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '4px solid transparent',
          borderRight: '4px solid transparent',
          borderTop: `4px solid ${tooltipBgColor}`,
        }}
      />
    </div>
  ) : null;

  // Tracks whether activeSyncs has surfaced a syncState for the current
  // mySyncId at least once. Without this guard, the cleanup effect below
  // races with the POST response: setMySyncId fires immediately, but the
  // sync-started SSE event has to flow through the server, the SSE
  // connection, and a setActiveSyncs state update before syncState
  // resolves. In prod that gap is large enough that the cleanup effect
  // sees mySyncId set, syncState undefined, and clears the claim before
  // the first event ever arrives — so the tooltip never shows.
  const hadSyncStateRef = useRef(false);

  const handleClick = async () => {
    const syncId = await onClick();
    if (syncId) {
      hadSyncStateRef.current = false;
      buttonSyncClaims.set(claimId, syncId);
      setMySyncId(syncId);
    }
  };

  // Drop the claim only on the defined→undefined transition — i.e. the
  // sync's event was evicted from activeSyncs after completion. See the
  // comment on hadSyncStateRef for why we can't just key off "syncState
  // is undefined".
  useEffect(() => {
    if (syncState) {
      hadSyncStateRef.current = true;
      return;
    }
    if (mySyncId && hadSyncStateRef.current) {
      buttonSyncClaims.delete(claimId);
      setMySyncId(undefined);
      hadSyncStateRef.current = false;
    }
  }, [mySyncId, syncState, claimId]);

  return (
    <div ref={buttonRef} style={{ display: 'inline-block' }}>
      <Button
        variant={hasError ? 'outline-danger' : variant}
        size={size}
        onClick={handleClick}
        disabled={disabled || isSyncing}
      >
        {isSyncing ? (
          <>
            <Spinner animation="border" size="sm" className="me-1" />
            Syncing...
          </>
        ) : hasError ? (
          <>
            <i className="bi bi-exclamation-triangle me-1"></i>
            Sync Error
          </>
        ) : (
          <>
            <i className={`bi ${icon} me-1`}></i>
            {buttonText}
          </>
        )}
      </Button>
      {tooltip && createPortal(tooltip, document.body)}
    </div>
  );
};
